import { useSubmission, useUnderwritingQuestions } from 'api/submission';
import {
  APPLICATION_STEPS,
  GOOGLE_TAG_EVENTS,
  META,
  QUOTE_FLOW_SECTION_KEYS,
  QUOTE_FLOW_SECTIONS,
} from 'common/constants';
import { pushToGoogleDataLayer, trackEventFullStory } from 'helpers/utils';

import { useChatWidgetStatus } from 'hooks/useChatWidgetStatus';
import { useStepperNavigation } from 'hooks/useStepperNavigation';
import DefaultLayout from 'layouts/defaultLayout';
import ExtendedLayout from 'layouts/extendedLayout';
import { includes, isEmpty } from 'lodash-es';
import { type FC, useEffect, useLayoutEffect, useMemo } from 'react';
import type { Timeout } from 'react-number-format/types/types';
import { useNavigate, useParams } from 'react-router-dom';
import { BusinessContact } from 'routes/BusinessContact';
import { DynamicStep } from 'routes/DynamicStep';
import { EligibilitySuccess } from 'routes/Eligibility/steps';
import { Claims } from 'routes/Exposures/Claims';
import { Drivers } from 'routes/Exposures/Drivers';
import { DrivingIncidents } from 'routes/Exposures/DrivingIncidents';
import { IndividualOwners } from 'routes/Exposures/IndividualOwners';
import Location from 'routes/Exposures/Location/Location';
import { Owners } from 'routes/Exposures/Owners';
import { Properties } from 'routes/Exposures/Properties';
import { Vehicles } from 'routes/Exposures/Vehicles';
import { WorkersCompensation } from 'routes/Exposures/WorkersCompensation';
import { Legal } from 'routes/Legal';
import { Purchase } from 'routes/Purchase';
import { Quote } from 'routes/Quote';
import { QuoteFailed } from 'routes/UnderwritingResults/QuoteFailed';
import { QuotePending } from 'routes/UnderwritingResults/QuotePending';
import { Referred } from 'routes/UnderwritingResults/Referred';
import { VehiclesPersonalInjuryProtection } from 'routes/VehiclesPersonalInjuryProtection';
import { useAppSelector } from 'store/hooks';
import { BiBerkCTA } from './components/BiBerkCTA';
import { TivlyOrBoldPenguinCTA } from './components/TivlyOrBoldPenguinCTA';

const SubmissionRenderer: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Initialize URL-based navigation
  useStepperNavigation();

  const { currentSection, currentStep } = useAppSelector('quoteStepper', [
    'currentSection',
    'currentStep',
  ]);
  const section = QUOTE_FLOW_SECTIONS[currentSection] || {};
  const steps = section.steps || [];
  const step = steps[currentStep] || {};

  const {
    data: submission,
    isLoading: isSubmissionLoading,
    isLoadingError,
    refetch,
  } = useSubmission(String(id), Boolean(id));

  /**
   * When there is an error while loading the submission initially, redirect to home
   * And by the submission status determine some functional redirects
   */
  useLayoutEffect(() => {
    switch (submission?.status) {
      case 'quoted':
      case 'accepted':
      // Sub-states of accepted: without them a cold load lands on the first application question.
      case 'signature_pending':
      case 'documents_pending':
        navigate(`?section=1&step=0`, { replace: true });
        break;

      case 'expired': {
        navigate(`/?expired=true`, { replace: true });
        break;
      }

      case 'discarded':
        window.location.href = META.HOME_LINK;
        break;

      case 'issued':
        navigate(`/submission/${id}/payment`, { replace: true });
        break;

      default: {
        if (isLoadingError) {
          navigate('/', { replace: true });
        }
        break;
      }
    }
  }, [submission, isLoadingError]);

  const fetchUwQuestions = !isEmpty(submission) && !isLoadingError && submission.status === 'draft';
  const { isLoading: isQuestionsLoading } = useUnderwritingQuestions(String(id), fetchUwQuestions);

  const isPageLoading = isSubmissionLoading || isQuestionsLoading;

  // Check if chat widget should be displayed
  const isChatWidgetAvailable = useChatWidgetStatus();

  const displayChatWidget = isChatWidgetAvailable && !isPageLoading;

  /**
   * If the submission status is quote_pending, refetch the submission every second
   * to check if the status has changed
   */
  useEffect(() => {
    let intervalId: Timeout | null = null;

    if (submission?.status === 'quote_pending') {
      intervalId = setInterval(() => refetch(), 1000);
    } else {
      switch (submission?.status) {
        case 'quoted':
          pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_QUOTED });
          trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_QUOTED);
          break;
        case 'declined':
          pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_DECLINED });
          trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_DECLINED);
          break;
        case 'referred':
          pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_REFERRED });
          trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_REFERRED);
          break;
        default:
          break;
      }
      if (includes(['quoted', 'declined', 'referred'], submission?.status)) {
        pushToGoogleDataLayer({ event: GOOGLE_TAG_EVENTS.LEAD_COMPLETE });
        trackEventFullStory(GOOGLE_TAG_EVENTS.LEAD_COMPLETE);
      }
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [submission?.status, refetch]);

  const renderSectionOrStep = useMemo(() => {
    if (section.key === QUOTE_FLOW_SECTION_KEYS.QUOTE) {
      return <Quote />;
    }

    if (section.key === QUOTE_FLOW_SECTION_KEYS.PURCHASE) {
      return <Purchase />;
    }

    switch (step.type) {
      case 'dynamic':
        return <DynamicStep step={step} />;

      case 'hardcoded': {
        switch (step.key) {
          case APPLICATION_STEPS.business_contact.key:
            return <BusinessContact />;

          case APPLICATION_STEPS.legal.key:
            return <Legal />;
          case APPLICATION_STEPS.owners.key:
            return <Owners />;

          case APPLICATION_STEPS.properties.key:
            return <Properties />;

          case APPLICATION_STEPS.vehicles.key:
            return <Vehicles />;

          case APPLICATION_STEPS.vehicles_injury_protection.key:
            return <VehiclesPersonalInjuryProtection />;

          case APPLICATION_STEPS.drivers.key:
            return <Drivers />;

          case APPLICATION_STEPS['driving-incidents'].key:
            return <DrivingIncidents />;

          case APPLICATION_STEPS.claims.key:
            return <Claims />;

          case APPLICATION_STEPS.location.key:
            return <Location />;

          case APPLICATION_STEPS.workers_compensation.key:
            return <WorkersCompensation />;

          case APPLICATION_STEPS.individual_owners.key:
            return <IndividualOwners />;

          default:
            return <></>;
        }
      }

      default:
        return <></>;
    }
  }, [currentSection, currentStep]);

  const renderSubmissionByStatus = useMemo(() => {
    const isApplicationSection = section.key === QUOTE_FLOW_SECTION_KEYS.APPLICATION;
    const isPurchaseSection = section.key === QUOTE_FLOW_SECTION_KEYS.PURCHASE;

    switch (submission?.status) {
      case 'declined':
        return (
          <DefaultLayout
            renderStepper={false}
            displayQuoteNumber={false}
            verticalCentered
            extendContainer
          >
            <TivlyOrBoldPenguinCTA submission={submission} />
          </DefaultLayout>
        );

      case 'referred':
        return <Referred />;

      case 'failed_to_quote':
        return <QuoteFailed />;

      case 'quote_pending':
        return <QuotePending />;

      case 'not_eligible': {
        const showBiBerkCTA = !!submission?.biberk_lander;

        if (showBiBerkCTA) {
          return (
            <DefaultLayout
              renderStepper={false}
              displayQuoteNumber={false}
              verticalCentered
              extendContainer
            >
              <BiBerkCTA submission={submission} />
            </DefaultLayout>
          );
        }

        return (
          <DefaultLayout
            renderStepper={false}
            displayQuoteNumber={false}
            verticalCentered
            extendContainer
          >
            <TivlyOrBoldPenguinCTA submission={submission} />
          </DefaultLayout>
        );
      }

      case 'draft': {
        if (isEmpty(submission.email)) {
          return (
            <DefaultLayout renderStepper={false} displayQuoteNumber>
              <EligibilitySuccess />
            </DefaultLayout>
          );
        } else {
          if (isApplicationSection) {
            return (
              <DefaultLayout
                displayQuoteNumber
                displaySaveAndExit
                renderStepper={!isPageLoading}
                displayChatWidget={displayChatWidget}
              >
                {renderSectionOrStep}
              </DefaultLayout>
            );
          }

          return (
            <ExtendedLayout displayChatWidget={displayChatWidget}>
              {renderSectionOrStep}
            </ExtendedLayout>
          );
        }
      }

      default: {
        if (isApplicationSection || isPurchaseSection) {
          return (
            <DefaultLayout
              displayQuoteNumber
              renderStepper={!isPageLoading}
              displayChatWidget={isPurchaseSection && displayChatWidget}
            >
              {renderSectionOrStep}
            </DefaultLayout>
          );
        }

        return (
          <ExtendedLayout displayChatWidget={displayChatWidget}>
            {renderSectionOrStep}
          </ExtendedLayout>
        );
      }
    }
  }, [
    submission?.status,
    submission?.email,
    isPageLoading,
    currentSection,
    currentStep,
    isSubmissionLoading,
  ]);

  return renderSubmissionByStatus;
};

export default SubmissionRenderer;
